import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext.tsx';
import { useProducts } from '../contexts/ProductsContext.tsx';
import analyticsService from '../services/AnalyticsService.ts';
import { wouldIncrementExceedLimit } from '../utils/marylandInfusedLimit.ts';
import BusinessIntelligenceService from '../services/BusinessIntelligenceService.ts';
import FrustrationDetectionService from '../services/FrustrationDetectionService.ts';
import { getCartMessageForStore } from '../services/cartMessageApiService.ts';
import { getPopupAlertForStore } from '../services/popupAlertApiService.ts';
import './Cart.css';

// Helper function to determine if this is Vermont store
const isVermontStore = (cart: any): boolean => {
  const currentUrl = window.location.pathname;
  const isVermontByUrl = currentUrl.includes('/6145') || currentUrl.includes('vermont') || currentUrl.includes('essex');
  const isVermontByEnv = cart?.retailerId === 'dcaee184-ca72-4e9f-a5fa-9c24613a0882';
  return isVermontByUrl || isVermontByEnv;
};


// Helper function to get display weight for cart items
const getDisplayWeight = (item: any, cart: any): string => {
  if (!item?.option) return 'N/A';
  
  const isVermont = isVermontStore(cart);
  
  if (isVermont && item.product?.category?.toLowerCase() === 'flower') {
    // For Vermont flower, show the actual variant option (e.g., "1G", "2G", "1/8OZ")
    return item.option.toUpperCase();
  }
  
  // For all other cases, show the original option
  return item.option;
};

// Helper function to determine if we should hide the separate price component
const shouldHidePrice = (item: any, cart: any): boolean => {
  // With the new variant cycling logic, we don't need to hide prices anymore
  // Each variant has its own specific price that should be displayed
  return false;
};

// Helper function to create visually ordered items that maintains stable positioning
const getVisuallyOrderedItems = (cart: any, variantTransitions: Map<string, any>, visualOrder: Map<string, number>): any[] => {
  if (!cart?.items) return [];
  
  const displayItems = [];
  
  // Add all current cart items with their visual positions
  cart.items.forEach((item) => {
    const productId = item.product?.id;
    if (productId) {
      const visualPosition = visualOrder.get(productId) ?? 999; // Items without visual order go to end
      displayItems.push({
        ...item,
        visualPosition
      });
    }
  });
  
  // Add transitioning items for products that are currently being changed
  for (const [productId, transitionItem] of variantTransitions) {
    // Check if this product already exists in cart (meaning the transition completed)
    const existingItem = displayItems.find(item => item.product?.id === productId);
    if (!existingItem) {
      // Product is missing from cart (during transition), show the transitioning item
      const visualPosition = visualOrder.get(productId) ?? 999;
      displayItems.push({
        ...transitionItem,
        visualPosition
      });
    }
  }
  
  // Sort by visual position to maintain stable order
  return displayItems.sort((a, b) => (a.visualPosition || 999) - (b.visualPosition || 999));
};

// Helper function to get quantity/weight display text for cart items
const getQuantityDisplayText = (item: any, cart: any): string => {
  if (!item) return '0';
  
  const isVermont = isVermontStore(cart);
  const isFlowerProduct = item.product?.category?.toLowerCase() === 'flower';
  
  if (isVermont && isFlowerProduct) {
    // For Vermont flower products, show the weight from the variant option
    return item.option?.toUpperCase() || '1G';
  }
  
  // For all other products, show the quantity number
  return String(item.quantity || 0);
};

// Vermont variant cycling helper functions
const getSortedVariants = (product: any): any[] => {
  if (!product?.variants || product.variants.length === 0) {
    return [];
  }
  
  return [...product.variants].sort((a, b) => {
    const weightA = a.flowerEquivalent?.value || parseFloat(a.option) || 0;
    const weightB = b.flowerEquivalent?.value || parseFloat(b.option) || 0;
    return weightA - weightB;
  });
};

const getNextVariant = (product: any, currentVariant: any): any => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0) return null;
  
  if (!currentVariant) {
    return sortedVariants[0]; // Start with smallest variant
  }
  
  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);
  
  // If we're at the last variant, cycle back to first
  return currentIndex !== -1 && currentIndex < sortedVariants.length - 1
    ? sortedVariants[currentIndex + 1]
    : sortedVariants[sortedVariants.length - 1];
};

const getPreviousVariant = (product: any, currentVariant: any): any => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0 || !currentVariant) return null;
  
  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);
  
  // If we're at the first variant, return null (which means remove from cart)
  return currentIndex > 0 ? sortedVariants[currentIndex - 1] : null;
};

// Check if current variant is the highest (largest) variant
const isAtMaxVariant = (product: any, currentVariant: any): boolean => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0 || !currentVariant) return false;
  
  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);
  return currentIndex === sortedVariants.length - 1; // Last item in sorted array is the largest
};

// Check if current variant is the lowest (smallest) variant
const isAtMinVariant = (product: any, currentVariant: any): boolean => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0 || !currentVariant) return false;
  
  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);
  return currentIndex === 0; // First item in sorted array is the smallest
};

interface CartProps {
  className?: string;
}

export default function Cart({ className }: CartProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    cart,
    isCartOpen,
    isLoading,
    error,
    currentRetailerId,
    updateQuantity,
    removeFromCart,
    addToCart,
    initiateCheckout,
    closeCart,
    clearError
  } = useCart();

  const { products: menuProducts } = useProducts();

  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [variantTransitions, setVariantTransitions] = useState<Map<string, any>>(new Map()); // productId -> temporary item data
  const [visualOrder, setVisualOrder] = useState<Map<string, number>>(new Map()); // productId -> visual position (stable ordering)
  
  // Cart abandonment tracking
  const [cartOpenTime, setCartOpenTime] = useState<number | null>(null);
  const [lastCartInteraction, setLastCartInteraction] = useState<number>(Date.now());
  const [exitIntentTracked, setExitIntentTracked] = useState<boolean>(false);

  // Maryland infused product limit error state
  const [infusedLimitError, setInfusedLimitError] = useState<string | null>(null);

  // Cart message state
  const [cartMessage, setCartMessage] = useState<{ message: string; mode: 'normal' | 'warning' } | null>(null);

  // Popup alert state
  const [popupAlert, setPopupAlert] = useState<{ message: string } | null>(null);

  // Business Intelligence Service
  const businessIntelligenceService = BusinessIntelligenceService.getInstance();
  
  // Frustration Detection Service
  const frustrationService = FrustrationDetectionService.getInstance();

  // Fetch cart message when cart has a retailer ID
  useEffect(() => {
    const fetchCartMessage = async () => {
      if (currentRetailerId) {
        try {
          const message = await getCartMessageForStore(currentRetailerId);
          setCartMessage(message);
        } catch (error) {
          setCartMessage(null);
        }
      }
    };

    fetchCartMessage();
  }, [currentRetailerId]);

  // Fetch popup alert when cart has a retailer ID
  useEffect(() => {
    if (!currentRetailerId) {
      setPopupAlert(null);
      return;
    }
    const fetchPopupAlert = async () => {
      try {
        const alert = await getPopupAlertForStore(currentRetailerId);
        setPopupAlert(alert);
      } catch (error) {
        setPopupAlert(null);
      }
    };

    fetchPopupAlert();
  }, [currentRetailerId]);

  // Track view_cart event when cart opens with detailed item data
  useEffect(() => {
    if (isCartOpen && cart && cart.items && cart.items.length > 0) {
      const cartTotal = cart.priceSummary?.total || 0;
      const cartTotalInDollars = cartTotal / 100; // Convert from cents
      
      // Prepare detailed items for view_cart event
      const items = cart.items.map((item, index) => ({
        item_id: item.productId || '',
        item_name: item.product?.name || '',
        item_category: item.product?.category || '',
        item_brand: item.product?.brand?.name || '',
        item_variant: item.option || '',
        price: item.basePrice || 0,
        quantity: item.quantity || 0,
        index: index
      }));

      // Track standard view_cart event for GA4 ecommerce
      analyticsService.trackEvent('view_cart', {
        value: cartTotalInDollars,
        currency: 'USD',
        items: items,
        cart_total_items: cart.items.reduce((sum, item) => sum + item.quantity, 0),
        store_id: cart.retailerId || ''
      });

      // Track cart open time for abandonment analysis
      setCartOpenTime(Date.now());
      setLastCartInteraction(Date.now());
      setExitIntentTracked(false); // Reset for new cart session
      
      // Track business intelligence cart action
      businessIntelligenceService.updateShoppingPattern('cart_action', cartTotalInDollars);
      
      // Mark high-value user session if cart value is significant
      if (cartTotalInDollars >= 100) {
        frustrationService.markHighValueUserAction('view_high_value_cart', cartTotalInDollars);
        frustrationService.markSessionReplayPoint('high_value_cart_opened', {
          cart_value: cartTotalInDollars,
          item_count: cart.items.length,
          frustration_score: frustrationService.getFrustrationScore()
        });
      }
    } else if (isCartOpen && (!cart || !cart.items || cart.items.length === 0)) {
      // Cart is open but empty - track empty cart view
      analyticsService.trackEvent('empty_cart_viewed', {
        store_id: cart?.retailerId || ''
      });
    }
  }, [isCartOpen, cart]);

  // Track cart abandonment when cart closes
  useEffect(() => {
    return () => {
      // Track abandonment when cart closes (component unmounts or cart closes)
      if (cartOpenTime && cart && cart.items && cart.items.length > 0) {
        const timeInCart = Date.now() - cartOpenTime;
        const timeSinceLastInteraction = Date.now() - lastCartInteraction;
        
        // Only track abandonment if user spent more than 5 seconds in cart
        if (timeInCart > 5000) {
          const cartTotal = cart.priceSummary?.total || 0;
          const cartTotalInDollars = cartTotal / 100;
          
          analyticsService.trackEvent('cart_abandoned', {
            value: cartTotalInDollars,
            currency: 'USD',
            time_in_cart: timeInCart,
            time_since_last_interaction: timeSinceLastInteraction,
            cart_total_items: cart.items.reduce((sum, item) => sum + item.quantity, 0),
            cart_unique_items: cart.items.length,
            abandonment_stage: timeSinceLastInteraction > 30000 ? 'inactive' : 'active',
            items: cart.items.map((item, index) => ({
              item_id: item.productId || '',
              item_name: item.product?.name || '',
              item_category: item.product?.category || '',
              item_brand: item.product?.brand?.name || '',
              item_variant: item.option || '',
              price: item.basePrice || 0,
              quantity: item.quantity || 0,
              index: index
            })),
            store_id: cart.retailerId || ''
          });
        }
      }
    };
  }, [cartOpenTime, lastCartInteraction, cart]);

  // Exit intent detection for cart abandonment
  useEffect(() => {
    if (!isCartOpen || !cart || !cart.items || cart.items.length === 0) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Detect exit intent (mouse leaving towards top of page)
      if (e.clientY <= 0 && !exitIntentTracked && cartOpenTime) {
        const timeInCart = Date.now() - cartOpenTime;
        
        // Only track if user has been in cart for more than 10 seconds
        if (timeInCart > 10000) {
          const cartTotal = cart.priceSummary?.total || 0;
          const cartTotalInDollars = cartTotal / 100;
          
          setExitIntentTracked(true);
          
          analyticsService.trackEvent('cart_exit_intent', {
            value: cartTotalInDollars,
            currency: 'USD',
            time_in_cart: timeInCart,
            cart_total_items: cart.items.reduce((sum, item) => sum + item.quantity, 0),
            cart_unique_items: cart.items.length,
            items: cart.items.map((item, index) => ({
              item_id: item.productId || '',
              item_name: item.product?.name || '',
              price: item.basePrice || 0,
              quantity: item.quantity || 0,
              index: index
            })),
            store_id: cart.retailerId || ''
          });
        }
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isCartOpen, cart, exitIntentTracked, cartOpenTime]);
  
  // Maintain visual order for stable cart item positioning
  useEffect(() => {
    if (cart?.items) {
      setVisualOrder(prevVisualOrder => {
        const newVisualOrder = new Map(prevVisualOrder);

        // Process current cart items
        cart.items.forEach((item, index) => {
          const productId = item.product?.id;
          if (productId) {
            // Only assign visual position if this product doesn't have one yet
            if (!newVisualOrder.has(productId)) {
              // Find the next available position
              const existingPositions = Array.from(newVisualOrder.values());
              const nextPosition = existingPositions.length > 0 ? Math.max(...existingPositions) + 1 : index;
              newVisualOrder.set(productId, nextPosition);
            }
          }
        });

        // Clean up visual order for products no longer in cart (unless they're transitioning)
        const productsInCart = new Set(cart.items.map(item => item.product?.id).filter(Boolean));
        for (const [productId] of newVisualOrder) {
          const stillInCart = productsInCart.has(productId);
          const isTransitioning = variantTransitions.has(productId);

          if (!stillInCart && !isTransitioning) {
            newVisualOrder.delete(productId);
          }
        }

        return newVisualOrder;
      });
    }
  }, [cart?.items, variantTransitions]);

  // Handle cart item click to open product detail via deep link
  const handleItemClick = (productId: string) => {
    if (!productId) {
      return;
    }

    // Set the productId in the URL to open the product detail drawer
    const params = new URLSearchParams(searchParams);
    params.set('productId', productId);
    setSearchParams(params);

    // Close cart after drawer has had time to detect URL change and open
    setTimeout(() => {
      closeCart();
    }, 500);
  };

  // Only PICKUP is supported - no order type selection needed

  const handleVermontVariantChange = async (itemId: string, direction: 'increase' | 'decrease') => {
    setLastCartInteraction(Date.now());
    
    const cartItem = cart?.items?.find(item => item.id === itemId);
    if (!cartItem || !cartItem.product) return;

    const product = cartItem.product;
    
    // Visual order should already be established for this product - no need to update it
    // The variant change preserves the existing visual position
    
    // Find current variant by matching the cart item's option
    const currentVariant = product.variants?.find(v => v.option === cartItem.option);
    if (!currentVariant) return;

    // Use a stable key based on product ID to track this operation
    const productKey = `variant-change-${product.id}`;
    setUpdatingItems(prev => new Set(prev).add(productKey));
    
    try {
      if (direction === 'increase') {
        const nextVariant = getNextVariant(product, currentVariant);
        if (nextVariant && nextVariant.id !== currentVariant.id) {
          // Store transitioning item to prevent visual gap
          setVariantTransitions(prev => new Map(prev).set(product.id, {
            ...cartItem,
            option: nextVariant.option,
            basePrice: nextVariant.priceRec || nextVariant.priceMed || 0,
            isTransitioning: true
          }));
          
          // Remove current item and add new variant
          await removeFromCart(itemId);
          await addToCart(product, nextVariant, 1);
        }
      } else {
        const previousVariant = getPreviousVariant(product, currentVariant);
        if (previousVariant) {
          // Store transitioning item to prevent visual gap
          setVariantTransitions(prev => new Map(prev).set(product.id, {
            ...cartItem,
            option: previousVariant.option,
            basePrice: previousVariant.priceRec || previousVariant.priceMed || 0,
            isTransitioning: true
          }));
          
          // Remove current item and add previous variant
          await removeFromCart(itemId);
          await addToCart(product, previousVariant, 1);
        } else {
          // Remove item completely (we're at the smallest variant)
          await removeFromCart(itemId);
        }
      }
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(productKey);
        return next;
      });
      
      // Clear the transitioning item
      setVariantTransitions(prev => {
        const next = new Map(prev);
        next.delete(product.id);
        return next;
      });
    }
  };

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    // Find the item being updated
    const itemToUpdate = cart?.items?.find(item => item.id === itemId);
    
    // Check if this is Vermont flower product - use variant cycling instead
    if (isVermontStore(cart) && itemToUpdate?.product?.category?.toLowerCase() === 'flower') {
      const currentQuantity = itemToUpdate?.quantity || 0;
      const direction = newQuantity > currentQuantity ? 'increase' : 'decrease';
      await handleVermontVariantChange(itemId, direction);
      return;
    }

    // Maryland recreational infused product limit check (12g flower equivalent max)
    const oldQuantity = itemToUpdate?.quantity || 0;

    if (wouldIncrementExceedLimit(cart, menuProducts, itemToUpdate?.productId, oldQuantity, newQuantity)) {
      setInfusedLimitError('Adding more would exceed the 12g limit for infused products');
      // Auto-dismiss after 3 seconds
      setTimeout(() => setInfusedLimitError(null), 3000);
      return;
    }

    // Update last interaction time for abandonment tracking
    setLastCartInteraction(Date.now());
    
    if (newQuantity < 1) {
      await handleRemoveItem(itemId);
      return;
    }

    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      await updateQuantity(itemId, newQuantity);
      
      // Track cart quantity update event
      if (itemToUpdate && oldQuantity !== newQuantity) {
        const newCartTotal = cart?.priceSummary?.total || 0;
        const itemPrice = itemToUpdate.basePrice || 0;
        
        analyticsService.trackEvent('cart_quantity_updated', {
          product_id: itemToUpdate.productId || '',
          product_name: itemToUpdate.product?.name || '',
          old_quantity: oldQuantity,
          new_quantity: newQuantity,
          quantity_change: newQuantity - oldQuantity,
          item_price: itemPrice,
          cart_total_items: cart?.items?.reduce((sum, item) => 
            sum + (item.id === itemId ? newQuantity : item.quantity), 0) || 0,
          cart_total_value: newCartTotal,
          store_id: cart?.retailerId || ''
        });
      }
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    // Update last interaction time for abandonment tracking
    setLastCartInteraction(Date.now());
    
    // Find the item being removed for analytics
    const itemToRemove = cart?.items?.find(item => item.id === itemId);
    
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      await removeFromCart(itemId);
      
      // Track remove from cart event
      if (itemToRemove) {
        const remainingTotal = cart?.items
          ?.filter(item => item.id !== itemId)
          ?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        
        analyticsService.trackRemoveFromCart({
          product_id: itemToRemove.productId || '',
          product_name: itemToRemove.product?.name || '',
          quantity: itemToRemove.quantity || 0,
          price: itemToRemove.basePrice || 0,
          cart_total_items: remainingTotal,
          cart_total_value: (cart?.priceSummary?.total || 0) - (itemToRemove.basePrice || 0) * (itemToRemove.quantity || 0),
          store_id: cart?.retailerId || ''
        });
      }
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleCheckout = async () => {
    // Track begin checkout event with enhanced item details
    if (cart) {
      const cartTotal = cart.priceSummary?.total || 0;
      const cartTotalInDollars = cartTotal / 100; // Convert from cents
      
      // Prepare detailed items for checkout tracking
      const items = cart.items?.map((item, index) => ({
        item_id: item.productId || '',
        item_name: item.product?.name || '',
        item_category: item.product?.category || '',
        item_brand: item.product?.brand?.name || '',
        item_variant: item.option || '',
        price: item.basePrice || 0,
        quantity: item.quantity || 0,
        index: index
      })) || [];
      
      // Enhanced checkout step tracking - Step 1: Begin Checkout
      analyticsService.trackCheckoutProgress({
        checkout_step: 1,
        checkout_option: 'PICKUP',
        step_name: 'cart',
        currency: 'USD',
        value: cartTotalInDollars,
        items: items
      });
      
      // Track standard begin_checkout
      analyticsService.trackBeginCheckout({
        cart_total_items: cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0,
        cart_total_value: cartTotalInDollars,
        checkout_method: 'PICKUP',
        store_id: cart.retailerId || ''
      });
      
      // Also track enhanced ecommerce begin_checkout event
      analyticsService.trackEvent('begin_checkout', {
        value: cartTotalInDollars,
        currency: 'USD',
        items: items,
        coupon: cart.appliedCoupon || undefined,
        store_id: cart.retailerId || ''
      });
    }

    try {
      await initiateCheckout('PICKUP'); // Always PICKUP since delivery not supported
    } catch (error) {
      // Track checkout failure for analytics
      const cartTotal = cart?.priceSummary?.total || 0;
      const cartTotalInDollars = cartTotal / 100;
      
      analyticsService.trackEvent('checkout_failed', {
        error_message: error instanceof Error ? error.message : 'Unknown checkout error',
        cart_value: cartTotalInDollars,
        cart_items: cart?.items?.length || 0,
        store_id: cart?.retailerId || '',
        error_type: error instanceof Error && error.message.includes('not yet implemented') ? 'not_implemented' : 'api_error'
      });
      
      // Error handling is managed by CartContext
    }
  };

  // Helper function to get fallback pricing from localStorage
  const getFallbackPrice = (productId: string, option: string): number | null => {
    try {
      const fallbacks = JSON.parse(localStorage.getItem('cartPricingFallbacks') || '[]');
      const fallback = fallbacks.find((fb: any) => 
        fb.productId === productId && fb.option === option
      );
      
      if (fallback && cart) {
        // Return the appropriate price based on cart pricing type
        const price = cart.pricingType === 'MEDICAL' ? fallback.priceMed : fallback.priceRec;
        return price;
      }
    } catch (error) {
      // Silently handle fallback errors
    }
    return null;
  };

  // Helper function to get original pricing for discount display
  const getOriginalPricing = (item: any): { regularPrice: number; specialPrice?: number } | null => {
    try {
      const itemKey = `${item?.productId}-${item?.option}`;
      const storedPricing = localStorage.getItem(`pricing_${itemKey}`);
      
      if (storedPricing) {
        const pricing = JSON.parse(storedPricing);
        return {
          regularPrice: pricing.regularPrice,
          specialPrice: pricing.specialPrice
        };
      }
    } catch (error) {
      // Silently handle pricing errors
    }
    return null;
  };

  // Helper function to get the effective base price (either from item or fallback)
  const getEffectiveBasePrice = (item: any): number => {
    let basePrice = item?.basePrice || 0;
    const quantity = item?.quantity || 1;

    // If basePrice is 0 or null, try to get fallback price
    if (!basePrice || basePrice === 0) {
      const fallbackPrice = getFallbackPrice(item?.productId, item?.option);
      if (fallbackPrice) {
        basePrice = fallbackPrice;
      }
    }

    // Apply the same pricing logic as getItemTotal to ensure consistency
    const originalPricing = getOriginalPricing(item);
    if (originalPricing && originalPricing.specialPrice && originalPricing.regularPrice > originalPricing.specialPrice) {
      // Use the special price directly for discounted items (this is the per-unit price)
      basePrice = originalPricing.specialPrice;
    } else {
      // For items with deals (which might have basePrice as total), convert to per-unit
      // Check if this item might have specialRewardIds by looking at basePrice behavior
      if (quantity > 1 && basePrice > 0 && basePrice % quantity === 0 && basePrice >= (quantity * 100)) {
        // If basePrice is exactly divisible by quantity and reasonably large, it might be a total
        const perUnitPrice = basePrice / quantity;
        basePrice = perUnitPrice;

        // Only convert from cents to dollars if the per-unit price seems to be in cents format
        if (basePrice >= 1000 && basePrice % 1 === 0) {
          basePrice = basePrice / 100;
        }
      } else if (basePrice >= 1000 && basePrice % 1 === 0 && !originalPricing?.specialPrice) {
        // Only convert from cents to dollars if the price seems to be in cents format
        // Check if price is likely in cents (whole number >= 1000, which would be $10+ in cents)
        basePrice = basePrice / 100;
      }
    }

    return basePrice;
  };

  const formatPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined || isNaN(price)) {
      return '$0.00';
    }

    // Just format as dollars - assume API returns correct values
    return `$${price.toFixed(2)}`;
  };

  // Format cart summary values (subtotal, taxes, total) which come from API in cents
  const formatCartSummaryPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined || isNaN(price)) {
      return '$0.00';
    }

    // Cart summary values from API are in cents, convert to dollars
    const dollarAmount = price / 100;
    return `$${dollarAmount.toFixed(2)}`;
  };

  // Component to display cart item pricing with potential discount
  const CartItemPrice: React.FC<{ item: any }> = ({ item }) => {
    const originalPricing = getOriginalPricing(item);
    const effectivePrice = getEffectiveBasePrice(item);

    if (originalPricing && originalPricing.specialPrice && originalPricing.regularPrice > originalPricing.specialPrice) {
      // Has special pricing, show crossed-out regular price and special price
      return (
        <div className="cart-item-price">
          PRICE: <span className="cart-item-price-value">
            <span className="cart-price-regular">{formatPrice(originalPricing.regularPrice)}</span>
            <span className="cart-price-special">{formatPrice(originalPricing.specialPrice)}</span>
            <span> each</span>
          </span>
        </div>
      );
    }

    // Regular pricing
    return (
      <div className="cart-item-price">
        PRICE: <span className="cart-item-price-value">{formatPrice(effectivePrice)} each</span>
      </div>
    );
  };

  const getItemTotal = (item: any): number => {
    // Use getEffectiveBasePrice which already handles all pricing logic including special pricing
    const basePrice = getEffectiveBasePrice(item);
    const quantity = item?.quantity || 0;
    return basePrice * quantity;
  };

  // Returns the retail line total + discounted line total, in dollars.
  // Works for both percent/dollar-off (basePrice = special) and BOGO
  // (basePrice = retail per-unit, line discount comes from item.discounts.total).
  const getLineItemTotals = (item: any): { original: number; discounted: number } => {
    const quantity = item?.quantity || 0;
    const discount = (item?.discounts?.total || 0) / 100;
    const originalPricing = getOriginalPricing(item);

    // Prefer the known retail per-unit when we have it (percent/dollar-off case).
    // For BOGO, originalPricing is null because CartContext only stores when
    // specialPrice exists, and basePrice from Dutchie is already the retail per-unit.
    const retailPerUnit =
      originalPricing?.regularPrice && originalPricing.regularPrice > 0
        ? originalPricing.regularPrice
        : getEffectiveBasePrice(item);

    const original = retailPerUnit * quantity;
    const discounted = Math.max(0, original - discount);
    return { original, discounted };
  };

  return (
    <>
      {/* Mobile Cart Backdrop */}
      {isCartOpen && (
        <div 
          className="cart-backdrop"
          onClick={closeCart}
        />
      )}
      
      {/* Cart Sidebar */}
      <div className={`cart-sidebar ${isCartOpen ? 'open' : ''} ${className || ''}`}>
        {/* Cart Header */}
        <div className="cart-header">
          <div className="cart-title">
            <span>YOUR CART</span>
            {cart && cart.items.length > 0 && (
              <span className="cart-count">({cart.items.length})</span>
            )}
          </div>
          <button 
            className="cart-close-btn"
            onClick={closeCart}
            aria-label="Close cart"
          >
            ✕
          </button>
        </div>

        {/* Cart Content */}
        <div className="cart-content">
          {error && (
            <div className="cart-error">
              <span>{error}</span>
              <button onClick={clearError} className="error-close">✕</button>
            </div>
          )}

          {/* Maryland infused product limit error */}
          {infusedLimitError && (
            <div className="cart-error">
              <span>{infusedLimitError}</span>
              <button onClick={() => setInfusedLimitError(null)} className="error-close">✕</button>
            </div>
          )}

          {isLoading && !cart && (
            <div className="cart-loading">
              <div className="loading-text">Loading cart...</div>
            </div>
          )}

          {!cart && !isLoading && (
            <div className="cart-empty">
              <div className="empty-cart-message">
                <p>Your cart is empty</p>
                <p className="empty-cart-subtitle">Add items from the menu to get started</p>
              </div>
            </div>
          )}

          {cart && cart.items.length === 0 && (
            <div className="cart-empty">
              <div className="empty-cart-message">
                <p>Your cart is empty</p>
                <p className="empty-cart-subtitle">Add items from the menu to get started</p>
              </div>
            </div>
          )}

          {cart && cart.items.length > 0 && (
            <>
              {/* Cart Items */}
              <div className="cart-items">
                {getVisuallyOrderedItems(cart, variantTransitions, visualOrder).map((item) => (
                  <div key={item.isTransitioning ? `transitioning-${item.product?.id}` : item.id} className="cart-item">
                      <div className="cart-item-content">
                        {/* Item Details - Clickable to open product detail */}
                        <div
                          className="cart-item-details clickable-cart-item"
                          onClick={() => handleItemClick(item?.productId)}
                          style={{ cursor: 'pointer' }}
                          title="Click to view product details"
                        >
                        <div className="cart-item-name">{item?.product?.name || 'Unknown Product'}</div>
                        <div className="cart-item-brand">
                          BRAND: <span className="cart-item-brand-value">{item?.product?.brand?.name || 'Unknown Brand'}</span>
                        </div>
                        <div className="cart-item-option">
                          WEIGHT: <span className="cart-item-option-value">{getDisplayWeight(item, cart)}</span>
                        </div>
                        {!shouldHidePrice(item, cart) && <CartItemPrice item={item} />}
                        {!item?.valid && item?.errors?.length > 0 && (
                          <div className="cart-item-errors">
                            {item.errors.map((error, index) => (
                              <div key={index} className="cart-item-error">{error}</div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Quantity Controls */}
                      <div className="cart-item-controls">
                        <div className="quantity-controls">
                          {(() => {
                            // Check if this is Vermont flower for special button logic
                            const isVermontFlower = isVermontStore(cart) && item?.product?.category?.toLowerCase() === 'flower';
                            const isUpdating = updatingItems.has(item?.id) || updatingItems.has(`variant-change-${item?.product?.id}`);
                            
                            // For Vermont flower, get current variant to check limits
                            let currentVariant = null;
                            let atMinVariant = false;
                            let atMaxVariant = false;
                            
                            if (isVermontFlower && item?.product && item?.option) {
                              currentVariant = item.product.variants?.find(v => v.option === item.option);
                              if (currentVariant) {
                                atMinVariant = isAtMinVariant(item.product, currentVariant);
                                atMaxVariant = isAtMaxVariant(item.product, currentVariant);
                              }
                            }
                            
                            return (
                              <>
                                <button
                                  className="quantity-btn"
                                  onClick={() => handleQuantityChange(item?.id, (item?.quantity || 0) - 1)}
                                  disabled={isUpdating || isLoading || (isVermontFlower && atMinVariant)}
                                  aria-label="Decrease quantity"
                                >
                                  −
                                </button>
                                <span className="quantity-display">
                                  {isUpdating ? '...' : getQuantityDisplayText(item, cart)}
                                </span>
                                <button
                                  className="quantity-btn"
                                  onClick={() => handleQuantityChange(item?.id, (item?.quantity || 0) + 1)}
                                  disabled={isUpdating || isLoading || (isVermontFlower && atMaxVariant)}
                                  aria-label="Increase quantity"
                                >
                                  +
                                </button>
                              </>
                            );
                          })()}
                        </div>
                        
                        <div className="cart-item-total">
                          {item?.discounts?.total > 0 ? (() => {
                            const { original, discounted } = getLineItemTotals(item);
                            return (
                              <div className="item-total-with-discount">
                                <span className="item-total-original">{formatPrice(original)}</span>
                                <span className="item-total-discounted">{formatPrice(discounted)}</span>
                              </div>
                            );
                          })() : (
                            formatPrice(getItemTotal(item))
                          )}
                        </div>
                        
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveItem(item?.id)}
                          disabled={updatingItems.has(item?.id) || isLoading}
                          aria-label="Remove item"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="cart-summary">
                <div className="cart-summary-row">
                  <span>Subtotal:</span>
                  <span>{formatCartSummaryPrice(cart.priceSummary?.subtotal)}</span>
                </div>
                
                {(cart.priceSummary?.discounts || 0) > 0 && (
                  <div className="cart-summary-row discount">
                    <span>Discounts:</span>
                    <span>-{formatCartSummaryPrice(cart.priceSummary?.discounts)}</span>
                  </div>
                )}
                
                <div className="cart-summary-row">
                  <span>Taxes:</span>
                  <span>{formatCartSummaryPrice(cart.priceSummary?.taxes)}</span>
                </div>
                
                {(cart.priceSummary?.fees || 0) > 0 && (
                  <div className="cart-summary-row">
                    <span>Fees:</span>
                    <span>{formatCartSummaryPrice(cart.priceSummary?.fees)}</span>
                  </div>
                )}
                
                <div className="cart-summary-total">
                  <span>Total:</span>
                  <span>{formatCartSummaryPrice(cart.priceSummary?.total)}</span>
                </div>
              </div>

              {/* Cart Message */}
              {cartMessage && (
                <div className={`cart-message cart-message-${cartMessage.mode}`}>
                  {cartMessage.message}
                </div>
              )}

              {/* Popup Alert */}
              {popupAlert && (
                <div className="cart-message cart-popup-alert">
                  {popupAlert.message}
                </div>
              )}

              {/* Pricing Type Display */}
              <div className="cart-pricing-type">
                <span className="pricing-type-label">
                  {cart.pricingType === 'MEDICAL' ? 'Medical' : 'Recreational'} Pricing
                </span>
              </div>

              {/* Order Type Display - PICKUP only */}
              <div className="order-type-display">
                <div className="order-type-label">Order Type: <span className="order-type-value">PICKUP</span></div>
              </div>

              {/* Checkout Button */}
              <div className="cart-checkout">
                <button 
                  className="checkout-btn" 
                  disabled={isLoading}
                  onClick={handleCheckout}
                >
                  {isLoading ? 'Processing...' : 'PROCEED TO CHECKOUT'}
                </button>
                <div className="checkout-note">
                  * This will redirect to complete your order
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}